/**
 * ModernFusion — content model + resolver.
 *
 * Urban Indian engagement: minimalist, glassmorphism, playlist features.
 */

import type { InvitationData } from '../types';

function img(id: string, w = 1400): string {
  return `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;
}

const STOCK = {
  hero: img('1519225421980-715cb0215aed', 2000),
  couple: img('1529636798458-92182e662485', 900),
  venue: img('1464366400600-7168b8af9bc3', 900),
  party: img('1533174072545-e8d4aa97edf9', 900),
};

export interface Event {
  title: string;
  date: string;
  time: string;
  venue: string;
  description: string;
  dressCode?: string;
}

export interface ModernContent {
  hero: {
    kicker: string;
    title: string;
    subtitle: string;
    host: string;
    dateLabel: string;
    venueShort: string;
    background: string;
  };
  story: {
    label: string;
    title: string;
    chapters: { title: string; description: string }[];
  };
  events: {
    label: string;
    title: string;
    items: Event[];
  };
  rsvp: {
    label: string;
    title: string;
    description: string;
    deadline: string;
    dietaryNote?: string;
    externalLink?: string;
  };
  footer: {
    message: string;
    signature: string;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────

const hasText = (v?: string | null): v is string =>
  typeof v === 'string' && v.trim().length > 0;

function formatLong(iso?: string): string {
  if (!hasText(iso)) return 'Date to be announced';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

function formatTime(iso?: string, fallback = ''): string {
  if (!hasText(iso)) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

// ── Demo Content ───────────────────────────────────────────────────────────

const DEMO: ModernContent = {
  hero: {
    kicker: 'Two souls, one journey',
    title: 'Rohan & Ananya',
    subtitle: 'are celebrating their engagement',
    host: 'The Mehta & Kapoor Families',
    dateLabel: 'Saturday, 12 April 2025',
    venueShort: 'The Glasshouse, Bangalore',
    background: STOCK.hero,
  },
  story: {
    label: 'Our Story',
    title: 'From classmates to forever',
    chapters: [
      { title: 'First Meeting', description: 'Met at design school in 2018' },
      { title: 'The Proposal', description: 'A sunset proposal in Goa, 2024' },
      { title: 'The Celebration', description: 'Join us for our engagement' },
    ],
  },
  events: {
    label: 'Celebrations',
    title: 'A weekend of joy',
    items: [
      {
        title: 'Cocktail Evening',
        date: 'Friday, 11 April 2025',
        time: '7:00 PM',
        venue: 'Sky Lounge, The Glasshouse',
        description: 'Meet and greet with drinks and music',
        dressCode: 'Smart Casual',
      },
      {
        title: 'Engagement Ceremony',
        date: 'Saturday, 12 April 2025',
        time: '6:00 PM',
        venue: 'The Glasshouse, Bangalore',
        description: 'Ring exchange followed by dinner and dancing',
        dressCode: 'Indian Fusion',
      },
    ],
  },
  rsvp: {
    label: 'Join Us',
    title: 'Kindly Respond',
    description: 'We would love to celebrate with you. Please let us know by the date below.',
    deadline: '25 March 2025',
    dietaryNote: 'Please mention any dietary preferences',
  },
  footer: {
    message: 'With love and excitement',
    signature: 'Rohan & Ananya',
  },
};

// ── Resolver ──────────────────────────────────────────────────────────────

export function resolveContent(data: InvitationData): ModernContent {
  const wedding = data.customData?.wedding ?? {};
  const [nameA, nameB] = (data.eventName || '').split(/\s*(?:&|and)\s*/i).filter(Boolean);
  const coupleNames = nameA && nameB ? `${nameA} & ${nameB}` : DEMO.hero.title;

  const scheduleEvents: Event[] | undefined = wedding.weddingEvents?.length
    ? wedding.weddingEvents.map((e: any, i: number) => ({
        title: e.title,
        date: formatLong(e.date),
        time: [formatTime(e.startTime), formatTime(e.endTime)].filter(Boolean).join(' – '),
        venue: e.venue,
        description: e.description || '',
        dressCode: e.dressCode || 'Smart Casual',
      }))
    : undefined;

  return {
    hero: {
      kicker: DEMO.hero.kicker,
      title: coupleNames,
      subtitle: DEMO.hero.subtitle,
      host: hasText(data.hostName) ? data.hostName : DEMO.hero.host,
      dateLabel: hasText(data.eventDate) ? formatLong(data.eventDate) : DEMO.hero.dateLabel,
      venueShort: hasText(data.eventVenue) ? data.eventVenue : DEMO.hero.venueShort,
      background: data.mediaUrls?.[0] ?? DEMO.hero.background,
    },
    story: DEMO.story,
    events: {
      ...DEMO.events,
      items: scheduleEvents ?? DEMO.events.items,
    },
    rsvp: {
      ...DEMO.rsvp,
      description: hasText(data.customMessage) ? data.customMessage : DEMO.rsvp.description,
      externalLink: hasText(data.rsvpLink) ? data.rsvpLink : undefined,
    },
    footer: {
      message: DEMO.footer.message,
      signature: coupleNames,
    },
  };
}
